import { useAtom, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { isStructureCorrosionReady } from '@/state';
import * as state from '@/components/Analysis/state';
import { poiState } from '@/components/Analysis/modules/pointOfInterest';
import {
  useGetStructureForInspectionDataLoaderQuery,
  useGetAllDecksQuery,
  useGetStructureLocationsForInspectionDataLoaderQuery,
  useGetAllPointOfInterestTemplatesQuery,
  useAllPointOfInterestsForDataLoaderQuery,
  PointOfInterestTemplateTypeEnum,
} from '@/__generated__/graphql';

import { useSetStructureRelationship } from '../../modules/pointCloud/useSetStructureRelationships';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useLocalStorage } from '@/hooks';
import { LocationsData } from '@/types';
import { sizeOfAnObjectInMb } from '@/utils/sizeOfAnObject';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { upperSnakeCase, urlToSVG } from '@/utils';
import useLiveSyncAssemblyPois from '../../shared/hooks/useLiveSyncAssemblyPois';
import { useCuboidState } from '../../modules/cuboids/hooks';

export const InspectionDataLoader = () => {
  const structureId = useGetStructureId();
  const cuboidState = useCuboidState();
  const [inspectionMetadata, setInspectionMetadata] = useAtom(state.inspectionMetadata);
  const [structureLocations, setStructureLocations] = useAtom(state.structureLocations);
  const setStructureDecks = useSetAtom(state.structureDecks);
  const setSelectedStructureDefectsLegends = useSetAtom(state.selectedStructureDefectsLegends);
  const setPoiTemplateIcons = useSetAtom(state.poiTemplateIcons);
  const setPartLegends = useSetAtom(state.partLegends);
  const setStructureBlisterHeightType = useSetAtom(state.structureBlisterHeightType);

  const setStructureLocationsMap = useSetAtom(state.structureLocationsMap);
  const setAllPointOfInterestTemplates = useSetAtom(state.allPointOfInterestTemplates);
  const setAllVoiTemplates = useSetAtom(state.allVoiTemplates);
  const setAllPOI = useSetAtom(poiState.structurePois);
  const setIsStructureCorrosionReady = useSetAtom(isStructureCorrosionReady);
  const setHasRgb = useSetAtom(state.hasRgb);
  const [locations, setLocations] = useLocalStorage<LocationsData | undefined>(
    `locations`,
    undefined
  );

  const setCuboidResizeStep = useSetAtom(cuboidState.cuboidResizeStep);

  const structureLocationsQueryResult = useGetStructureLocationsForInspectionDataLoaderQuery({
    variables: {
      structureId,
    },
    fetchPolicy: 'cache-first',
    skip: !!locations?.[structureId],
  });

  useEffect(() => {
    if (locations && locations[structureId]) {
      const structureLocationsMap = locations[structureId]?.reduce((map, location) => {
        map.set(location.id, location);
        return map;
      }, new Map());

      setStructureLocationsMap(structureLocationsMap);
    }
  }, [locations, structureId, setStructureLocationsMap]);

  useEffect(() => {
    const allLocations = structureLocationsQueryResult.data?.allLocations;
    if (allLocations) {
      if (sizeOfAnObjectInMb(allLocations) <= 2.5) {
        setLocations({ [structureId]: allLocations });
        return;
      }
      setStructureLocations(allLocations);
    }
  }, [
    setLocations,
    structureId,
    structureLocationsQueryResult.data?.allLocations,
    setStructureLocations,
  ]);

  useEffect(() => {
    if (locations?.[structureId] && !structureLocations)
      setStructureLocations(locations?.[structureId]);
  }, [locations, setStructureLocations, structureId, structureLocations]);

  const allPoiTemplatesQueryResult = useGetAllPointOfInterestTemplatesQuery({
    variables: {
      structureId,
    },
    skip: !structureId,
    fetchPolicy: 'cache-first',
  });

  const { data: pointOfInterests, refetch: refetchPois } = useAllPointOfInterestsForDataLoaderQuery(
    {
      variables: {
        input: {
          structureId,
          includeManuallyCreated: true,
        },
      },
      fetchPolicy: 'no-cache',
    }
  );

  const structureDecksQueryResult = useGetAllDecksQuery({
    variables: {
      structureId,
    },
    fetchPolicy: 'cache-first',
  });

  const structureQueryResult = useGetStructureForInspectionDataLoaderQuery({
    variables: {
      structureId,
    },
    fetchPolicy: 'cache-first',
    skip: !!inspectionMetadata,
    onCompleted: (response) => {
      const step = response?.structure?.config?.spatialHeatmapStep;

      if (
        step &&
        // really making no faulty data get's to the viewer
        typeof step.x === 'number' &&
        typeof step.y === 'number' &&
        typeof step.z === 'number'
      )
        setCuboidResizeStep([step.x, step.y, step.z]);
    },
  });

  useSetStructureRelationship(structureId, structureQueryResult.data?.structure);

  useEffect(() => {
    if (structureDecksQueryResult?.data?.allDecks) {
      setStructureDecks(structureDecksQueryResult?.data?.allDecks);
    }
  }, [setStructureDecks, structureDecksQueryResult?.data?.allDecks]);

  useEffect(() => {
    if (structureQueryResult?.data?.structure?.blisterHeightType) {
      setStructureBlisterHeightType(structureQueryResult?.data?.structure?.blisterHeightType);
    }
  }, [setStructureBlisterHeightType, structureQueryResult?.data?.structure?.blisterHeightType]);

  useLiveSyncAssemblyPois(() => {
    refetchPois().then((response) => {
      if (response.data?.pointOfInterests) {
        setAllPOI(response.data.pointOfInterests);
      }
    });
  });

  useEffect(() => {
    const allTemplates = allPoiTemplatesQueryResult.data?.allPointOfInterestTemplate;
    if (allTemplates) {
      const fetchData = async () => {
        const unresolvedSvgs = allTemplates?.map((template) => {
          return urlToSVG(getCloudfrontUrl(template?.icon?.name ?? ''));
        });

        try {
          const downloadedSvgs = await Promise.all(unresolvedSvgs);
          const svgData = allTemplates.map((template, index) => ({
            name: upperSnakeCase(template.name),
            icon: downloadedSvgs[index],
            color: template.icon?.color ?? '',
          }));
          setPoiTemplateIcons(svgData);
        } catch (error) {
          console.log(error);
        }
      };
      setAllPointOfInterestTemplates(allTemplates);
      setAllVoiTemplates(
        allTemplates
          .filter((template) => template.type === PointOfInterestTemplateTypeEnum.VolumeOfInterest)
          .map((template) => template.name)
      );
      fetchData();
    }
  }, [
    setAllPointOfInterestTemplates,
    allPoiTemplatesQueryResult.data?.allPointOfInterestTemplate,
    setPoiTemplateIcons,
    setAllVoiTemplates,
  ]);

  useEffect(() => {
    if (pointOfInterests?.pointOfInterests) {
      setAllPOI(pointOfInterests?.pointOfInterests);
    }
  }, [setAllPOI, pointOfInterests?.pointOfInterests]);

  useEffect(() => {
    const structureInfo = structureQueryResult.data?.structure;
    if (structureInfo) {
      setInspectionMetadata(structureInfo);
      setPartLegends(
        structureInfo.partLegends?.reduce<{ [key: string]: string }>((accumulator, item) => {
          accumulator[item.name.toLowerCase()] = item.color;
          return accumulator;
        }, {}) ?? {}
      );

      // Use Defect Legend from structure and add isEnabled flag in order to toggle the visibility of the defect
      setSelectedStructureDefectsLegends(
        structureInfo?.defectLegends?.map((defect) => {
          return {
            ...defect,
            isEnabled: false,
            legendText: defect.legendText ?? '',
          };
        })
      );

      // flag that indicates that this deployment octree has rgb data
      setHasRgb(structureQueryResult.data?.structure?.hasRGB || false);
    }
  }, [
    setIsStructureCorrosionReady,
    setInspectionMetadata,
    structureQueryResult.data?.structure,
    setSelectedStructureDefectsLegends,
    setHasRgb,
    setPartLegends,
  ]);

  return <></>;
};
